<template>
  <Sheet :open="open" @update:open="(value) => emit('update:open', value)">
    <SheetContent side="right" class="conversation-documents" data-test-id="chat-documents-sheet">
      <SheetHeader class="conversation-documents__header">
        <SheetTitle>{{ t('chat.context.documents') }}</SheetTitle>
        <SheetDescription v-if="conversationName">{{ conversationName }}</SheetDescription>
      </SheetHeader>

      <div class="conversation-documents__tools">
        <InputGroup>
          <InputGroupAddon>
            <Search aria-hidden="true" />
          </InputGroupAddon>
          <InputGroupInput
            v-model="searchRef"
            type="search"
            :placeholder="t('chat.documents.searchPlaceholder')"
            :aria-label="t('chat.documents.searchPlaceholder')"
            data-test-id="chat-documents-search"
          />
        </InputGroup>
        <div class="conversation-documents__filters" role="group" :aria-label="t('chat.documents.filterLabel')">
          <button
            v-for="filter in FILTERS"
            :key="filter"
            type="button"
            :aria-pressed="filterRef === filter"
            :class="cn('conversation-documents__filter', filterRef === filter && 'conversation-documents__filter--active')"
            :data-test-id="`chat-documents-filter-${filter}`"
            @click="filterRef = filter"
          >
            {{ t(`chat.documents.filter.${filter}`) }}
            <span class="conversation-documents__filter-count">{{ countsRef[filter] }}</span>
          </button>
        </div>
      </div>

      <div class="conversation-documents__body">
        <template v-if="groupsRef.length > 0">
          <section
            v-for="group in groupsRef"
            :key="group.key"
            class="conversation-documents__group"
            :aria-label="group.label"
          >
            <h3 class="conversation-documents__eyebrow">{{ group.label }}</h3>
            <ul v-if="group.images.length > 0" class="conversation-documents__grid">
              <li v-for="image in group.images" :key="image.id">
                <button
                  type="button"
                  class="conversation-documents__thumb"
                  :aria-label="t('chat.documents.open', { name: image.name })"
                  @click="openChatFile(image, image.ownerId)"
                >
                  <img
              v-if="image.signedUrl && !failedImagesRef.has(image.id)"
              :src="image.signedUrl"
              alt=""
              loading="lazy"
              @error="failedImagesRef.add(image.id)"
            />
            <ImageIcon v-else class="size-5" aria-hidden="true" />
                </button>
              </li>
            </ul>
            <ul v-if="group.files.length > 0" class="conversation-documents__files">
              <li v-for="file in group.files" :key="file.id" class="conversation-documents__file">
                <span class="conversation-documents__ext" aria-hidden="true">{{ file.ext.toUpperCase() }}</span>
                <span class="conversation-documents__file-text">
                  <span class="conversation-documents__file-name">{{ file.name }}</span>
                  <span class="conversation-documents__meta">{{ file.author }}, {{ file.dateLabel }}</span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  class="size-9 shrink-0"
                  :aria-label="t('chat.documents.download', { name: file.name })"
                  @click="downloadChatFile(file, file.ownerId)"
                >
                  <Download class="size-4" aria-hidden="true" />
                </Button>
              </li>
            </ul>
          </section>
        </template>
        <div v-else class="conversation-documents__empty" role="status">
          <span class="conversation-documents__empty-icon" aria-hidden="true"><Search class="size-5" /></span>
          <p class="conversation-documents__empty-title">
            {{ searchRef ? t('chat.documents.noResult', { query: searchRef }) : t('chat.documents.none') }}
          </p>
          <p v-if="searchRef" class="conversation-documents__meta">{{ t('chat.documents.noResultHint') }}</p>
          <Button v-if="searchRef" type="button" variant="outline" size="sm" @click="searchRef = ''">
            {{ t('chat.documents.clearSearch') }}
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { Download, ImageIcon, Search } from 'lucide-vue-next'
import { computed, reactive, ref, watch } from 'vue'

import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { ChatDocumentType } from '@/features/chat/utils/chat-documents'
import { downloadChatFile, openChatFile } from '@/features/chat/utils/open-chat-file'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'

const props = defineProps<{
  open: boolean
  documents: ChatDocumentType[]
  conversationName?: string
  initialFilter?: 'all' | 'images' | 'files'
}>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const { t } = useI18n()

// miniature introuvable ou expiree : on affiche l'icone a la place
const failedImagesRef = reactive(new Set<string>())

const FILTERS = ['all', 'images', 'files'] as const
const filterRef = ref<(typeof FILTERS)[number]>('all')
const searchRef = ref('')

// a chaque ouverture : filtre demande par le panneau de contexte, recherche videe
watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    filterRef.value = props.initialFilter ?? 'all'
    searchRef.value = ''
  },
)

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

const searchedRef = computed(() => {
  const query = normalize(searchRef.value.trim())
  if (!query) return props.documents
  return props.documents.filter((document) => normalize(`${document.name} ${document.author}`).includes(query))
})

const countsRef = computed(() => ({
  all: searchedRef.value.length,
  images: searchedRef.value.filter((document) => document.isImage).length,
  files: searchedRef.value.filter((document) => !document.isImage).length,
}))

// documents groupes par mois, les plus recents d'abord
const groupsRef = computed(() => {
  const visible = searchedRef.value.filter((document) =>
    filterRef.value === 'all' ? true : filterRef.value === 'images' ? document.isImage : !document.isImage,
  )
  const groups: { key: string; label: string; images: ChatDocumentType[]; files: ChatDocumentType[] }[] = []
  for (const document of visible) {
    const day = dayjsClient(document.sentAt)
    const key = day.format('YYYY-MM')
    let group = groups.find((item) => item.key === key)
    if (!group) {
      group = { key, label: day.format('MMMM YYYY'), images: [], files: [] }
      groups.push(group)
    }
    if (document.isImage) group.images.push(document)
    else group.files.push(document)
  }
  return groups
})
</script>

<style lang="scss">
@import './conversation-documents-sheet.scss';
</style>
